import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { AIReliabilityLabel } from './AIBadges';
import {
  Eye, Search, TrendingUp, ChevronRight, Sparkles, Trophy,
  Zap, BarChart3,
} from 'lucide-react';

/**
 * Iter-171 — Behavioral Metrics card.
 *
 * Visible to talent on dashboard. Positive coaching only:
 *   • Viewed by N clients this week
 *   • Appeared in X searches
 *   • Top Profile booked 2.7x faster
 *   • Response & reliability percentile
 *   • Profile Boost Suggestion of the Week
 */
const BehavioralMetricsCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/metrics/me');
        if (!cancelled) setData(r.data);
      } catch { /* talent-only; silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) return null;

  const delta = (cur, prev) => {
    if (!prev || prev === 0) return null;
    const pct = Math.round(((cur - prev) / prev) * 100);
    return pct;
  };
  const viewDelta = delta(data.views_7d, data.views_prev_7d);
  const impDelta  = delta(data.impressions_7d, data.impressions_prev_7d);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm" data-testid="behavioral-metrics-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-cyan-500" />
          <h3 className="font-semibold text-gray-900">This Week</h3>
        </div>
        {data.is_top_profile && (
          <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[11px]" data-testid="metrics-top-profile-badge">
            <Trophy className="w-3 h-3 mr-1" /> Top Profile
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-lg bg-cyan-50 border border-cyan-100 p-3" data-testid="metric-views">
          <div className="flex items-center gap-1.5 text-[11px] text-cyan-700 mb-0.5">
            <Eye className="w-3 h-3" /> Profile views
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.views_7d}</p>
          <p className="text-[11px] text-gray-500">
            {data.unique_clients_7d > 0
              ? `${data.unique_clients_7d} unique clients`
              : 'this week'}
            {viewDelta !== null && (
              <span className={`ml-1 font-medium ${viewDelta >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {viewDelta >= 0 ? '↑' : '↓'} {Math.abs(viewDelta)}%
              </span>
            )}
          </p>
        </div>
        <div className="rounded-lg bg-violet-50 border border-violet-100 p-3" data-testid="metric-impressions">
          <div className="flex items-center gap-1.5 text-[11px] text-violet-700 mb-0.5">
            <Search className="w-3 h-3" /> Search appearances
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.impressions_7d}</p>
          <p className="text-[11px] text-gray-500">
            this week
            {impDelta !== null && (
              <span className={`ml-1 font-medium ${impDelta >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {impDelta >= 0 ? '↑' : '↓'} {Math.abs(impDelta)}%
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        {data.response_rate_percent !== null && data.response_rate_percent !== undefined && (
          <div className="rounded-lg border border-gray-200 p-3" data-testid="metric-response-rate">
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-0.5">
              <TrendingUp className="w-3 h-3" /> Response rate
            </div>
            <p className="text-lg font-bold text-gray-900">{data.response_rate_percent}%</p>
            {data.response_rate_percent >= 80 && (
              <p className="text-[10px] text-emerald-600 font-medium">Above marketplace avg</p>
            )}
          </div>
        )}
        {data.reliability_score !== null && data.reliability_score !== undefined && (
          <div className="rounded-lg border border-gray-200 p-3" data-testid="metric-reliability">
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-0.5">
              <AIReliabilityLabel testId="metric-reliability-label" />
            </div>
            <p className="text-lg font-bold text-gray-900">{Number(data.reliability_score).toFixed(1)}<span className="text-sm text-gray-400">/5</span></p>
            {data.reliability_score >= 4.5 && (
              <p className="text-[10px] text-emerald-600 font-medium">Top tier</p>
            )}
          </div>
        )}
      </div>

      {!data.is_top_profile && (
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-100 p-3 mb-3" data-testid="top-profile-callout">
          <p className="text-xs text-gray-700">
            <strong>Top Profile</strong> talent get booked <strong>{data.top_profile_booked_x_faster}x faster</strong>.
            Reach <strong>85+</strong> profile quality to unlock the badge.
          </p>
        </div>
      )}

      {data.boost_suggestion && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-center gap-3" data-testid="boost-suggestion">
          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <Zap className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide">Boost suggestion</p>
            <p className="text-sm font-medium text-gray-900">{data.boost_suggestion.label}</p>
            <p className="text-[11px] text-gray-600 truncate">{data.boost_suggestion.hint}</p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="text-amber-700 shrink-0 text-xs"
            onClick={() => navigate('/talent/profile-info')}
            data-testid="boost-suggestion-action-btn"
          >
            +{data.boost_suggestion.weight} <ChevronRight className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default BehavioralMetricsCard;
